import { Badge } from './ui/badge.jsx';
import { ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';

/**
 * SLOImpactBadge — visualizes a rightsizing action's golden-signal outcome.
 *
 * Maps the backend's slo_status + slo_confidence to the colored chip Mukul
 * (Swiggy) asked for. Mirrors the slo_guard.py thresholds:
 *   ≥90 green "Verified safe"
 *   70-89 yellow "Within tolerance"
 *   <70  red    "Regression — review"
 *   null grey  "—"
 *
 * Hover tooltip exposes the math (latency Δ, error rate Δ, throughput Δ)
 * so a technical operator can see WHY the badge is the color it is —
 * trust comes from transparency.
 *
 * Props:
 *   sloStatus:      "observed_safe" | "observed_regression" | "observed_unavailable"
 *                   | "auto_rolled_back" | "not_evaluated" | undefined
 *   sloConfidence:  number | null
 *   sloPostApply:   array of checkpoint entries (latest is shown in tooltip)
 *   compact:        true to render the small-font variant (used in table rows)
 */
export default function SLOImpactBadge({
    sloStatus,
    sloConfidence,
    sloPostApply,
    compact = false,
}) {
    // The "no SLO config" / "feature off" path: render nothing rather than
    // a confusing grey badge. Operators on clusters without slo_config
    // shouldn't see SLO chrome.
    if (!sloStatus || sloStatus === 'not_evaluated') {
        return null;
    }

    const sizeClass = compact ? 'text-[10px] px-1.5 py-0 h-4' : 'text-xs';

    if (sloStatus === 'auto_rolled_back') {
        return (
            <Badge
                title="OptOps auto-rolled back this change after detecting an SLO regression at both +5min and +15min checkpoints."
                className={`${sizeClass} bg-orange-100 text-orange-800 border-orange-200 flex items-center gap-1`}
            >
                <ShieldAlert className="w-3 h-3" />
                <span>Auto-rolled back</span>
            </Badge>
        );
    }

    if (sloStatus === 'observed_unavailable' || sloConfidence == null) {
        return (
            <Badge
                title="No SLI samples in the post-apply window. Check that the agent's SLO scraper is reaching your Prometheus."
                className={`${sizeClass} bg-gray-100 text-gray-600 border-gray-200 flex items-center gap-1`}
            >
                <ShieldQuestion className="w-3 h-3" />
                <span>SLO data unavailable</span>
            </Badge>
        );
    }

    // Color tier: green ≥90 / yellow 70-89 / red <70.
    let tone, Icon, label;
    if (sloConfidence >= 90) {
        tone = 'bg-green-100 text-green-800 border-green-200';
        Icon = ShieldCheck;
        label = 'Verified safe';
    } else if (sloConfidence >= 70) {
        tone = 'bg-yellow-100 text-yellow-800 border-yellow-200';
        Icon = ShieldAlert;
        label = 'Within tolerance';
    } else {
        tone = 'bg-red-100 text-red-800 border-red-200';
        Icon = ShieldAlert;
        label = 'Regression — review';
    }

    return (
        <Badge
            title={buildTooltip(sloConfidence, sloPostApply)}
            className={`${sizeClass} ${tone} flex items-center gap-1`}
        >
            <Icon className="w-3 h-3" />
            <span>SLO {sloConfidence} · {label}</span>
        </Badge>
    );
}

/**
 * Build the operator-facing tooltip from the latest post-apply checkpoint.
 * Plain string for the native title="" attribute (matches the rest of the
 * codebase's tooltip convention — see confidenceBadge.js).
 */
export function buildTooltip(confidence, postApply) {
    if (!postApply || postApply.length === 0) {
        return `SLO confidence: ${confidence}`;
    }
    const latest = postApply[postApply.length - 1];
    const d = latest.delta_pct || {};
    const lines = [
        `SLO confidence: ${confidence}`,
        `Checkpoint: +${latest.minutes_since_apply}min`,
        `Verdict: ${latest.verdict}`,
        '─────────────────',
    ];
    if (d.latency_p95_ms != null)  lines.push(`Latency p95: ${formatPct(d.latency_p95_ms)}`);
    if (d.error_rate != null)      lines.push(`Error rate:  ${formatAbs(d.error_rate)}`);
    if (d.throughput_rps != null)  lines.push(`Throughput:  ${formatPct(d.throughput_rps)}`);
    return lines.join('\n');
}

function formatPct(v) {
    if (v == null) return '—';
    const sign = v >= 0 ? '+' : '';
    return `${sign}${v.toFixed(1)}%`;
}

function formatAbs(v) {
    if (v == null) return '—';
    const sign = v >= 0 ? '+' : '';
    return `${sign}${v.toFixed(4)}`;
}
